// src/components/layout/Topbar.jsx
// ✅ FULLY MODIFIED - Removed polling, added WebSocket listener

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { Bell, Search, Menu, X, Check, AlertCircle } from 'lucide-react';
import './Topbar.css';

const Topbar = ({ onMenuClick }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { request } = useApi();
  
  // Get WebSocket context
  const { 
    isConnected, 
    connectionError, 
    notifications, 
    setNotifications, 
    unreadCount, 
    setUnreadCount, 
    loading,
    fetchNotifications 
  } = useWebSocketContext();

  // ── State ────────────────────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const searchRef = useRef(null);
  const notificationRef = useRef(null);

  // ── Page Titles ─────────────────────────────────────────────
  const titles = {
    '/dashboard': 'Dashboard',
    '/onboarding': 'WhatsApp Onboarding',
    '/onboarding/callback': 'WhatsApp Onboarding',
    '/templates': 'Message Templates',
    '/phonebooks': 'Phonebooks',
    '/send': 'Send Campaign',
    '/products': 'Products',
    '/inbox': 'Inbox',
    '/reports': 'Reports',
    '/billing': 'Billing',
    '/admin': 'Admin Panel',
    '/admin/organizations': 'Organizations',
    '/admin/users': 'Users',
    '/admin/credits': 'Credits',
    '/notifications': 'Notifications',
  };

  const pageTitle = titles[pathname] || 'Dashboard';
  const orgName = localStorage.getItem('wa_org_name') || user?.organizationName || user?.orgName || 'System';

  // ── Mark as Read ────────────────────────────────────────────
  const markAsRead = async (id) => {
    try {
      await request('put', `/api/notifications/${id}/read`).catch(() => {});
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // ── Mark All as Read ────────────────────────────────────────
  const markAllAsRead = async () => {
    try {
      await request('put', '/api/notifications/read-all').catch(() => {});
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    }
  };

  // ── Clear All ────────────────────────────────────────────────
  const clearAllNotifications = async () => {
    try {
      await request('delete', '/api/notifications').catch(() => {});
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  // ── Handle Notification Click ──────────────────────────────
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setNotificationsOpen(false);
  };

  // ── Handle Search ──────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      console.log('Searching for:', searchTerm);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  // ── Click Outside ──────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── ✅ FIXED: Fetch only when dropdown opens (NO POLLING) ──
  useEffect(() => {
    if (notificationsOpen && fetchNotifications) {
      fetchNotifications();
    }
  }, [notificationsOpen]);

  // ── ✅ NEW: WebSocket status listener ────────────────────────
  useEffect(() => {
    if (isConnected) {
      console.log('✅ WebSocket connected - notifications will update in real-time');
    }
    if (connectionError) {
      console.warn('⚠️ WebSocket error - falling back to manual fetch');
    }
  }, [isConnected, connectionError]);

  // ── Get icon for notification type ─────────────────────────
  const getNotificationIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return <Check className="w-3 h-3" />;
      case 'error': return <AlertCircle className="w-3 h-3" />;
      case 'warning': return <AlertCircle className="w-3 h-3" />;
      default: return <Bell className="w-3 h-3" />;
    }
  };

  // ── Get color for notification type ──────────────────────
  const getNotificationColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      default: return '#2563EB';
    }
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="topbar-menu-btn" onClick={onMenuClick}>
          <Menu className="w-5 h-5" />
        </button>
        <div className="topbar-title-section">
          <h1 className="topbar-title">{pageTitle}</h1>
          <p className="topbar-subtitle">{orgName}</p>
          {/* WebSocket status indicator */}
          {isConnected && (
            <span className="ws-status connected">● Live</span>
          )}
          {connectionError && (
            <span className="ws-status disconnected">● Offline</span>
          )}
        </div>
      </div>

      <div className="topbar-right">
        {/* ── Search ────────────────────────────────────────────── */}
        <div className={`topbar-search-wrapper ${searchOpen ? 'active' : ''}`} ref={searchRef}>
          <form className="topbar-search" onSubmit={handleSearch}>
            <Search className="topbar-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="topbar-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchOpen(true)}
            />
            {searchTerm && (
              <button
                type="button"
                className="topbar-search-clear"
                onClick={() => setSearchTerm('')}
              >
                <X className="w-3 h-3" />
              </button>
            )}
            <button type="submit" className="topbar-search-submit">
              Search
            </button>
          </form>
        </div>

        {/* ── Notification ──────────────────────────────────────── */}
        <div className="topbar-notification-wrapper" ref={notificationRef}>
          <button
            className="topbar-icon-btn topbar-notifications"
            onClick={() => setNotificationsOpen(!notificationsOpen)}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="topbar-notification-dot">{unreadCount}</span>
            )}
          </button>

          {/* Notification Dropdown */}
          {notificationsOpen && (
            <div className="topbar-notification-dropdown">
              <div className="topbar-notification-header">
                <h4>Notifications</h4>
                <div className="topbar-notification-actions">
                  {notifications.length > 0 && unreadCount > 0 && (
                    <button onClick={markAllAsRead} className="mark-read-btn">
                      Mark all read
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button onClick={clearAllNotifications} className="clear-btn">
                      Clear all
                    </button>
                  )}
                </div>
              </div>

              <div className="topbar-notification-list">
                {loading ? (
                  <div className="notification-loading">
                    <div className="skeleton" style={{ height: 60, marginBottom: 8 }} />
                    <div className="skeleton" style={{ height: 60, marginBottom: 8 }} />
                    <div className="skeleton" style={{ height: 60 }} />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="empty-notifications">
                    <Bell size={40} />
                    <p>No notifications yet</p>
                    <span>We'll notify you when something happens</span>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif.id}
                      className={`notification-item ${!notif.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div
                        className="notification-icon"
                        style={{ background: getNotificationColor(notif.type) }}
                      >
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="notification-content">
                        <p>{notif.message}</p>
                        <span className="notification-time">{notif.time || 'Just now'}</span>
                      </div>
                      {!notif.read && <span className="unread-dot" />}
                    </div>
                  ))
                )}
              </div>

              {notifications.length > 0 && (
                <div className="topbar-notification-footer">
                  <button 
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }}
                  >
                    View all notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Avatar ────────────────────────────────────────────── */}
        <div className="topbar-avatar">
          {(user?.name || 'U')[0].toUpperCase()}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
